import capitalize from 'lodash/capitalize'
import { getStampLabel, getStampType, SelectedMultiplierCard } from './cards'

/** `unit` defaults to points before a card is chosen, matching the design */
export const getUnit = (
  card?: SelectedMultiplierCard | null
): 'points' | 'stamps' =>
  getStampLabel(card) === 'stamps' ? 'stamps' : 'points'

const getUnitLabel = (card?: SelectedMultiplierCard | null) =>
  capitalize(getUnit(card))

/** The muted suffix after a card's name: "Points per £" for a spend-based card, "Stamps per product" for an item-based one */
export const cardEarnLabel = (card?: SelectedMultiplierCard | null) =>
  getStampType(card ?? undefined) === 'value'
    ? `${getUnitLabel(card)} per £`
    : `${getUnitLabel(card)} per product`

const isItemBased = (card?: SelectedMultiplierCard | null) =>
  getStampType(card ?? undefined) === 'product'

/** An item-based card only earns on its own products, so "all products" means those */
export const allProductsSummary = (card?: SelectedMultiplierCard | null) =>
  isItemBased(card)
    ? `Every product that earns ${getUnit(card)} on ${
        card?.name || 'this card'
      } gets the multiplier`
    : 'Every product in the menu earns the multiplier'

export const allProductsRecap = (card?: SelectedMultiplierCard | null) =>
  isItemBased(card)
    ? `any product that earns ${getUnit(card)}`
    : 'everything they buy'

export const categoriesPickerSubtitle = (
  card?: SelectedMultiplierCard | null
) =>
  isItemBased(card)
    ? `Products added to these categories later will be eligible too, if they earn ${getUnit(
        card
      )} on ${card?.name || 'this card'}.`
    : 'Any products added to selected categories will automatically be eligible for this multiplier.'

export const loyaltyCardHelperText =
  'Applies only to customers collecting on this card'

/** The card select's helper text, naming how many other multipliers the card already has */
export const loyaltyCardHint = (
  cardName: string,
  otherMultipliersCount: number
) => {
  if (otherMultipliersCount === 0) {
    return `No other multipliers are linked to ${cardName} yet`
  }
  if (otherMultipliersCount === 1) {
    return `1 other multiplier is already linked to ${cardName}`
  }
  return `${otherMultipliersCount} other multipliers are already linked to ${cardName}`
}

export const factorBoxTitle = 'MULTIPLIER'

export const factorBoxDescription =
  'Multiply what customers earn on qualifying items'

export const applyMultiplierToDescription =
  'Decides whether modifiers are multiplied too.'

/** "Apply multiplier to" on an item-based card, where the control is shown but locked */
export const applyMultiplierToLockedDescription = (
  card?: SelectedMultiplierCard | null
) =>
  `${card?.name ?? 'This card'} earns ${getUnit(
    card
  )} on selected products, so modifiers can't be included`

/** "2× on qualifying items" — the review card's Multiplier field */
export const reviewFactor = (factor: number) => `${factor}× on qualifying items`

/** Shown when a save comes back with a `category_ids`/`product_ids` error and the stale ids have been dropped from the form */
export const staleTargetsMessage =
  "Some selected items are no longer available, so we've removed them. Check your selection and save again."

/** Shown under the card select for any `loyalty_card_id` save error — the BE says "does not exist" for archived and not-owned cards alike */
export const cardUnavailableMessage = 'This card is no longer available'

/** Shown when `GetLoyaltyCard` fails or returns no card, so the category and product pickers can't open */
export const cardLookupFailedMessage =
  "Couldn't load this card's products. Refresh the page to try again."

/** List filter options */
export const FILTER_LABELS = {
  published: 'Published',
  archived: 'Archived'
} as const

/** Row menu */
export const ROW_MENU_EDIT = 'Edit'
export const ROW_MENU_PAUSE = 'Pause'
export const ROW_MENU_RESUME = 'Resume'
export const ROW_MENU_ARCHIVE = 'Archive'

const UNSAVED_OTHER_CHANGES = " Your other changes aren't saved yet."

/** `withOtherChanges`: the edit form has unsaved edits */
export const pausedToast = (name: string, withOtherChanges = false) =>
  `${name} is paused.${withOtherChanges ? UNSAVED_OTHER_CHANGES : ''}`

export const resumedToast = (name: string, withOtherChanges = false) =>
  `${name} is switched back on.${withOtherChanges ? UNSAVED_OTHER_CHANGES : ''}`

export const archiveDialogTitle = (name: string) => `Archive ${name}?`

export const archiveDialogBody = (withUnsavedWarning = false) =>
  `It stops earning points straight away and moves to your archived multipliers. Points customers have already earned stay on their cards. You can restore it at any time.${
    withUnsavedWarning ? ' Your unsaved changes will be lost.' : ''
  }`

export const ARCHIVE_DIALOG_CONFIRM = 'Archive'

export const archivedToast = (name: string) => `${name} archived`
export const VIEW_ARCHIVED_LABEL = 'View archived'

export const ARCHIVED_ON_HEADER = 'Archived on'
export const RESTORE_BUTTON_LABEL = 'RESTORE'

export const ARCHIVED_EMPTY_TITLE = 'No archived multipliers'
export const ARCHIVED_EMPTY_BODY =
  'When you archive a multiplier, it moves here. You can restore it at any time.'

/** Restore toast on the list */
export const restoredToast = (name: string) => `${name} restored`

export const ARCHIVED_BANNER_TITLE = 'This multiplier is archived'

export const archivedBannerBody = (active: boolean) =>
  `It isn't earning points. Restore it to edit it or switch it back on. It comes back ${
    active ? 'switched on' : 'paused'
  }, as it was before you archived it.`

/** Edit-screen restore toast; `active` comes from the response */
export const restoredEditToast = (name: string, active: boolean) =>
  `${name} restored. It's ${
    active ? 'switched on' : 'paused'
  }, as it was before you archived it.`

export const ARCHIVE_FOOTER_LABEL = 'ARCHIVE'

export const pauseErrorToast = (name: string) =>
  `Couldn't pause ${name}. Please try again.`
export const resumeErrorToast = (name: string) =>
  `Couldn't switch ${name} back on. Please try again.`
export const archiveErrorToast = (name: string) =>
  `Couldn't archive ${name}. Please try again.`
export const restoreErrorToast = (name: string) =>
  `Couldn't restore ${name}. Please try again.`
